/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import React, { useId, useMemo } from 'react';

import { css as customCss } from '@compiled/react';

import { css, cssMap, jsx } from '@atlaskit/css';
import Tile from '@atlaskit/tile/tile';

import type { ThirdPartyLogoProps } from './types';

const glyphStyles = css({
	display: 'block',
	width: '100%',
	height: '100%',
});

const hexagonSizeStyles = cssMap({
	xxsmall: { width: '16px', height: '16px' },
	xsmall: { width: '20px', height: '20px' },
	small: { width: '24px', height: '24px' },
	medium: { width: '32px', height: '32px' },
	large: { width: '40px', height: '40px' },
	xlarge: { width: '48px', height: '48px' },
});

const hexagonRootStyles = css({
	display: 'inline-flex',
	flexShrink: 0,
	verticalAlign: 'middle',
});

const hexagonStyles = css({
	display: 'inline-flex',
	boxSizing: 'border-box',
	width: '100%',
	height: '100%',
	clipPath:
		'polygon(45% 1.33975%, 46.5798% 0.60307%, 48.26352% 0.15192%, 50% 0%, 51.73648% 0.15192%, 53.4202% 0.60307%, 55% 1.33975%, 89.64102% 21.33975%, 91.06889% 22.33956%, 92.30146% 23.57212%, 93.30127% 25%, 94.03794% 26.5798%, 94.48909% 28.26352%, 94.64102% 30%, 94.64102% 70%, 94.48909% 71.73648%, 94.03794% 73.4202%, 93.30127% 75%, 92.30146% 76.42788%, 91.06889% 77.66044%, 89.64102% 78.66025%, 55% 98.66025%, 53.4202% 99.39693%, 51.73648% 99.84808%, 50% 100%, 48.26352% 99.84808%, 46.5798% 99.39693%, 45% 98.66025%, 10.35898% 78.66025%, 8.93111% 77.66044%, 7.69854% 76.42788%, 6.69873% 75%, 5.96206% 73.4202%, 5.51091% 71.73648%, 5.35898% 70%, 5.35898% 30%, 5.51091% 28.26352%, 5.96206% 26.5798%, 6.69873% 25%, 7.69854% 23.57212%, 8.93111% 22.33956%, 10.35898% 21.33975%)',
});

const hexagonBorderStyles = customCss({
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage -- `color.border` is not an allowed @atlaskit/css background token.
	backgroundColor: 'var(--ds-border)',
	// Padding preserves the clipped hexagon shape; a CSS border renders as a rectangle.
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage -- `border.width` is not an allowed @atlaskit/css padding token and needs a 1px fallback.
	paddingBlock: 'var(--ds-border-width, 1px)',
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage -- `border.width` is not an allowed @atlaskit/css padding token and needs a 1px fallback.
	paddingInline: 'var(--ds-border-width, 1px)',
});

const hexagonContentStyles = customCss({
	display: 'block',
	width: '100%',
	height: '100%',
	// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage -- Agent logos use fixed light-theme SVG fills and require the same theme-independent white background as Tile.
	backgroundColor: '#FFF',
	clipPath: 'inherit',
});

const svgIdAttributePattern = /\sid=(["'])([^"']+)\1/g;

const getSvgIdNamespace = (instanceId: string): string =>
	`third-party-logo-${instanceId.replace(/[^a-zA-Z0-9_-]/g, '-')}`;

/**
 * Third-party SVGs can contain gradients, masks, and clipPaths with document-wide IDs.
 * Namespacing each render keeps repeated logos from resolving `url(#id)` references to the
 * first matching SVG definition elsewhere on the page.
 */
const namespaceSvgIds = (markup: string, namespace: string): string => {
	const ids = Array.from(markup.matchAll(svgIdAttributePattern), ([, , id]) => id);
	const uniqueIds = Array.from(new Set(ids));

	return uniqueIds.reduce((updatedMarkup, id) => {
		const namespacedId = `${namespace}-${id}`;
		const escapedId = escapeRegExp(id);

		return updatedMarkup
			.replace(new RegExp(`\\sid=(["'])${escapedId}\\1`, 'g'), ` id=$1${namespacedId}$1`)
			.replace(new RegExp(`url\\(#${escapedId}\\)`, 'g'), `url(#${namespacedId})`)
			.replace(
				new RegExp(`(\\s(?:href|xlink:href)=["'])#${escapedId}(["'])`, 'g'),
				`$1#${namespacedId}$2`,
			);
	}, markup);
};

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

type ThirdPartyLogoWrapperProps = ThirdPartyLogoProps & {
	markup: string;
};

/**
 * __ThirdPartyLogoWrapper__
 *
 * Internal wrapper used to render full-bleed third-party logo assets inside ADS Tile.
 */
export function ThirdPartyLogoWrapper({
	markup,
	size = 'medium',
	label = '',
	testId,
	type = 'app',
}: ThirdPartyLogoWrapperProps): React.JSX.Element {
	const instanceId = useId();
	const namespacedMarkup = useMemo(
		() => namespaceSvgIds(markup, getSvgIdNamespace(instanceId)),
		[markup, instanceId],
	);

	const glyph = (
		<span
			css={glyphStyles}
			aria-hidden="true"
			dangerouslySetInnerHTML={{ __html: namespacedMarkup }}
		/>
	);

	if (type === 'agent') {
		return (
			<span
				css={[hexagonRootStyles, hexagonSizeStyles[size]]}
				data-testid={testId}
				aria-label={label !== '' ? label : undefined}
				role={label !== '' ? 'img' : undefined}
			>
				<span css={[hexagonStyles, hexagonBorderStyles]}>
					<span css={hexagonContentStyles}>{glyph}</span>
				</span>
			</span>
		);
	}

	return (
		<Tile
			// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage -- Tile's backgroundColor API accepts the semantic `white` value.
			backgroundColor="white"
			label={label}
			size={size}
			testId={testId}
			hasBorder
			isInset={false}
		>
			{glyph}
		</Tile>
	);
}
